"use client";

import { useCallback, useState, useEffect, useRef, KeyboardEvent } from "react";
import SubmissionForm from "@/components/SubmissionForm";
import SubmissionTracker from "@/components/SubmissionTracker";
import RatingShareModal from "@/components/RatingShareModal";
import { useAuth } from "@/context/AuthContext";
import { useApi } from "@/context/ApiContext";
import { useAdobeAnalytics } from "@/hooks/useAdobeAnalytics";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { getImageURL } from "@/utils/cdn";
import {
  Submission,
  SiteControls,
  SiteSettings,
  Episode,
  Rating,
} from "@/lib/api/types";

// --- TYPES ---

interface LocalSubmission {
  id: string;
  timestamp?: number;
  isRated?: boolean;
  reviewed?: boolean;
}

interface SubmissionWithLocal extends Submission {
  timestamp?: number;
  isRated?: boolean;
  reviewed?: boolean;
  image?: string;
}

interface HydratedState {
  siteControls: SiteControls;
  ratings: Rating[];
  episode: Episode | null;
  siteSettings: SiteSettings;
  userTakes: Submission[];
}

// --- HELPER FUNCTIONS ---

// Safely gets the list of submission IDs from the browser's localStorage.
const getLocalSubmissions = (): LocalSubmission[] => {
  try {
    if (typeof window === "undefined") return [];
    const subs = localStorage.getItem("userSubmissions");
    const allSubs: LocalSubmission[] = subs ? JSON.parse(subs) : [];

    const onlyRecentTakes = Date.now() - 72 * 60 * 60 * 1000;

    // Filter out old or un-timestamped submissions.
    const recentAndUnrated = allSubs.filter((sub) => {
      const isRecent = sub.timestamp ? sub.timestamp > onlyRecentTakes : false;

      return isRecent && !sub.isRated;
    });

    // Update the local list
    localStorage.setItem("userSubmissions", JSON.stringify(recentAndUnrated));

    return recentAndUnrated;
  } catch (e) {
    console.error("Failed to parse submissions from localStorage", e);
    return [];
  }
};

// The top half of the homepage
interface HomePageProps {
  showSlug: string;
}

export default function HomePage({ showSlug }: HomePageProps) {
  // --- HOOKS ---
  // Standard React and Next.js hooks for routing and authentication.
  const { user, loading } = useAuth();
  const apiClient = useApi();
  const router = useRouter();
  const { trackEvent } = useAdobeAnalytics();

  // --- STATE MANAGEMENT ---
  // Single hydrated state object containing all server data
  const [hydratedState, setHydratedState] = useState<HydratedState | null>(
    null
  );
  const [isLoading, setIsLoading] = useState<boolean>(true);
  // Holds the list of submission objects from the user's browser.
  const [localSubmissions, setLocalSubmissions] = useState<LocalSubmission[]>(
    []
  );
  // Track whether we've loaded localStorage yet
  const [hasLoadedLocalStorage, setHasLoadedLocalStorage] = useState<boolean>(false);
  // Holds the full, real-time data for the user's submissions from Firestore.
  const [userSubmissions, setUserSubmissions] = useState<SubmissionWithLocal[]>(
    []
  );
  // Holds the data for the submission currently displayed in the rating modal.
  const [modalSubmission, setModalSubmission] =
    useState<SubmissionWithLocal | null>(null);
  // Added Form Scrolling Reference
  const formSectionRef = useRef<HTMLDivElement | null>(null);
  const [triggerScrollOnLoad, setTriggerScrollOnLoad] =
    useState<boolean>(false);

  // --- DATA FETCHING & SIDE EFFECTS (useEffect) ---

  // Effect to load the initial list of submission IDs from localStorage on mount.
  useEffect(() => {
    const localSubmissions = getLocalSubmissions();
    if (localSubmissions.length) {
      // use has submitted something before, put them back into the form when it's ready
      setTriggerScrollOnLoad(true);
    }
    setLocalSubmissions(localSubmissions);
    setHasLoadedLocalStorage(true);
  }, []);


  // Effect to load all hydrated state in a single API call
  // Only runs after localStorage has been loaded
  useEffect(() => {
    // Don't fetch until we've loaded localStorage
    if (!hasLoadedLocalStorage) {
      return;
    }

    const loadHydratedState = async () => {
      try {
        // Get all hydrated data in one call: site controls, ratings, episode, and user takes
        const data = await apiClient.user.getHydratedState({
          // get show slug from URL
          showSlug,
          userTakesIds: localSubmissions.map((s) => s.id),
        });

        // Set the entire hydrated state object at once
        setHydratedState(data);

        // Hydrate user submissions with rating data from response
        // Spread localStorage data first, then server data to ensure server data takes precedence
        const hydratedSubmissions: SubmissionWithLocal[] = data.userTakes.map(
          (take) => {
            const localData = localSubmissions.find((local) => local.id === take.id);
            return {
              ...localData,
              ...take,
              // Keep reviewed status from localStorage if it exists
              reviewed: localData?.reviewed || false,
            };
          }
        );
        setUserSubmissions(hydratedSubmissions);

        setIsLoading(false);
      } catch (error) {
        console.error("Error loading hydrated state:", error);
        setIsLoading(false);
      }
    };

    loadHydratedState();
  }, [apiClient, localSubmissions, showSlug, hasLoadedLocalStorage]);

  // Effect to poll for submission status updates every minute
  useEffect(() => {
    const pollSubmissionStatus = async () => {
      if (localSubmissions.length === 0) {
        return;
      }

      try {
        // Use the hydrated state endpoint to refresh user takes
        const data = await apiClient.user.getHydratedState({
          showSlug,
          userTakesIds: localSubmissions.map((s) => s.id),
        });

        // Update user submissions with fresh data
        const hydratedSubmissions: SubmissionWithLocal[] = data.userTakes.map(
          (take) => {
            const localData = localSubmissions.find((local) => local.id === take.id);
            return {
              ...localData,
              ...take,
              // Keep reviewed status from localStorage if it exists
              reviewed: localData?.reviewed || false,
            };
          }
        );
        setUserSubmissions(hydratedSubmissions);
      } catch (error) {
        console.error("Error polling submission status:", error);
      }
    };

    // Only start polling if we have submissions
    if (localSubmissions.length > 0) {
      // Set up interval to poll every minute
      const intervalId = setInterval(pollSubmissionStatus, 60000);

      // Clean up interval on unmount or when dependencies change
      return () => clearInterval(intervalId);
    }

    return 
  }, [localSubmissions, apiClient, showSlug]);

  // Effect to redirect unauthenticated users if the page is protected.
  useEffect(() => {
    // This check now waits for hydrated state to load, then checks the live setting from Firestore.
    if (hydratedState?.siteControls.isHomepageProtected && !loading && !user) {
      router.push("/login");
    }
  }, [user, loading, router, hydratedState]);

  // --- EVENT HANDLERS ---

  // — Form Scrolling to top of box ---
  /**
   ** This function is passed to the SubmissionForm and is called whenever*
   ** the form's internal step changes, triggering a scroll.*
   */
  const handleFormStepChange = () => {
    if (formSectionRef.current) {
      formSectionRef.current.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  /**
   * Called from SubmissionForm on a successful submission.
   * Adds the new submission to localStorage and updates state.
   */
  const onSubmissionSuccess = (newSubmission: LocalSubmission) => {
    const currentSubs = getLocalSubmissions();

    // Add the new submission and a timestamp to the list.
    const updatedSubs = [
      ...currentSubs,
      { ...newSubmission, timestamp: Date.now() },
    ];

    // Save the updated list back to localStorage and to the component's state.
    localStorage.setItem("userSubmissions", JSON.stringify(updatedSubs));
    setLocalSubmissions(updatedSubs);
  };

  /**
   * Opens the modal for a specific submission (or the first rated one if no ID provided).
   */
  const handleShowModal = (submissionId?: string) => {
    trackEvent("Tracker", "See Rating Card");
    
    let submissionToShow: SubmissionWithLocal | undefined;
    
    if (submissionId) {
      // Show the specific submission requested
      submissionToShow = userSubmissions.find(s => s.id === submissionId);
    } else {
      // Find first rated and unviewed submission (legacy behavior)
      submissionToShow = userSubmissions.find(
        (s) => (s.rating || s.specialRating) && !s.reviewed
      );
    }
    
    if (submissionToShow) {
      setModalSubmission(submissionToShow);
    }
  };

  /**
   * Set the ref and trigger initial page scroll once the formSection element has rendered
   */
  const setFormSectionRef = useCallback(
    (node: HTMLDivElement | null) => {
      formSectionRef.current = node;
      if (node && triggerScrollOnLoad && !isLoading) {
        // prevent this from triggering more than once
        setTriggerScrollOnLoad(false);
        // delay to avoid scrolling while page content is shifting
        setTimeout(() => {
          node.scrollIntoView({ behavior: "smooth", block: "start" });
        }, 250);
      }
    },
    [triggerScrollOnLoad, isLoading]
  );

  /**
   * Closes the rating modal and marks the submission as "reviewed".
   */
  const handleCloseModal = async () => {
    if (!modalSubmission) return;

    const viewedId = modalSubmission.id;

    // Update localStorage to persist the "reviewed" state.
    const localSubs = getLocalSubmissions();
    const updatedSubs = localSubs.map((s) =>
      s.id === viewedId ? { ...s, reviewed: true } : s
    );
    localStorage.setItem("userSubmissions", JSON.stringify(updatedSubs));
    setLocalSubmissions(updatedSubs);

    // Update the main state object immediately for a snappy UI.
    setUserSubmissions((prev) =>
      prev.map((s) => (s.id === viewedId ? { ...s, reviewed: true } : s))
    );
    setModalSubmission(null);

    // Call the API to mark as viewed for potential analytics.
    try {
      await apiClient.user.markAsViewed({ submissionId: viewedId });
    } catch (error) {
      console.error("Error marking submission as viewed:", error);
    }
  };

  /**
   * Handles share event tracking from the modal.
   */
  const handleShare = (platform: string) => {
    trackEvent("Share Card", platform);
  };

  // ACCESSIBILITY: Keyboard handler for table rows
  const handleTableRowKeyDown = (
    e: KeyboardEvent<HTMLTableRowElement>,
    sub: SubmissionWithLocal
  ) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      setModalSubmission(sub);
    }
  };

  // --- RENDER LOGIC ---

  if (loading || !hydratedState) {
    return <p>Loading...</p>;
  }

  // Next, if protection is ON and the user is logged out, continue showing a blank/loading page.
  // The useEffect hook we fixed earlier will handle the actual redirect to the login screen.
  if (hydratedState.siteControls.isHomepageProtected && !user) {
    return <p>Loading...</p>;
  }

  // Filter submissions into two lists for display right before rendering.
  // Show all submissions in the tracker (both reviewed and unreviewed)
  const allSubmissionsForTracker = userSubmissions;
  const reviewedSubmissions = userSubmissions.filter(
    (s) => s.reviewed && s.isRated
  );
  const areSubmissionOpen =
    hydratedState.siteControls.isTakingSubmissions &&
    hydratedState.episode !== null &&
    hydratedState.episode.displayPercentage < 100;

  return (
    <div className="font-['Helvetica_Now',_Arial,_sans-serif]">
      {/* ================================================================================= */}
      {/* HERO SECTION (TOP)                                                              */}
      {/* ================================================================================= */}
      {/* --- WRAPPER FOR TWO-COLUMN DESKTOP LAYOUT --- */}
      <div className="flex flex-col items-stretch lg:flex-row">
        {/* --- LOGO DIV --- */}
        <div className="relative">
          <div className="absolute right-4 top-4 z-10 lg:left-6 lg:right-auto lg:top-6">
            <img src={getImageURL("/images/siriusxm.png")} alt="SiriusXM Logo" className="w-[120px]" />
          </div>
        </div>
        {/* --- COLUMN 1: HERO & STATUS SECTION --- */}
        <div 
          className="relative flex-1 bg-[#1a1a1a] bg-cover bg-center bg-no-repeat px-0 pb-4 pt-16 text-center text-white lg:py-8"
          style={{
            backgroundImage: `url('${getImageURL(
              hydratedState.siteSettings.heroBackgroundImage || 
              "/images/SAS-gradient-BG-1200x800_low.jpg"
            )}')`
          }}
        >
          <div className="flex flex-col justify-center lg:min-h-screen lg:pb-[10vh] lg:sticky lg:top-0">
            <div className="mx-auto w-[500px] max-w-[90%] lg:max-w-[80%]">
              <img
                src={getImageURL(
                  hydratedState.siteSettings.imageLogoLight ||
                  "/images/getSerious onDark.png"
                )}
                alt="Logo"
                className="mx-0 my-1 max-w-[90%] lg:max-w-[90%]"
              />
              <div className="rounded-2xl bg-white/10 px-[15px] py-[10px]">
                <p className="m-0 text-xs font-normal lg:text-[0.9rem]">
                  {hydratedState.siteSettings.homeTaglineSecondary}
                </p>
                <p className="m-0 text-xl font-extrabold lg:text-5xl">
                  {hydratedState.siteSettings.homeTagline}
                </p>
                {hydratedState.episode &&
                  (hydratedState.episode.topic ||
                    hydratedState.episode.topicDescription ||
                    hydratedState.episode.topicImageURL) && (
                    <div className="mx-auto my-0 p-0">
                      {hydratedState.episode.topicImageURL && (
                        <img
                          src={getImageURL(hydratedState.episode.topicImageURL)}
                          alt="Topic"
                          className="mb-2 max-w-full rounded-lg lg:mb-4"
                        />
                      )}
                      {hydratedState.episode.topic && (
                        <h2 className="m-0 p-0 text-sm font-bold text-[#ffc107] lg:text-xl">
                          Topic: {hydratedState.episode.topic}
                        </h2>
                      )}
                      {hydratedState.episode.topicDescription && (
                        <p className="mt-1 p-0 text-xs font-normal text-[#ced4da] lg:text-[0.9rem]">
                          {hydratedState.episode.topicDescription}
                        </p>
                      )}
                    </div>
                  )}
                <div>
                  <hr className="my-2 border-t border-white/20 lg:my-4" />
                  <p className="m-0 py-1 text-xs font-medium lg:text-[0.9rem]">
                    Submissions are{" "}
                    <span className={areSubmissionOpen ? "text-[#5cb85c]" : "text-[#d9534f]"}>
                      {areSubmissionOpen ? "OPEN" : "CLOSED"}
                    </span>
                  </p>

                  {hydratedState.siteControls.displayRateBar &&
                    hydratedState.siteControls.isTakingSubmissions &&
                    hydratedState.episode &&
                    (() => {
                      const remainingPercentage = Math.max(
                        0,
                        100 - hydratedState.episode.displayPercentage
                      );

                      return (
                        <div>
                          <p className="m-0 p-0 text-xs font-normal lg:text-sm">
                            {hydratedState.siteSettings.submissionLimitDescription}
                          </p>
                          <div className="mx-auto mt-2 h-[20px] max-w-[400px] overflow-hidden rounded-md bg-[#343a40] lg:mt-3 lg:h-[25px]">
                            <div
                              className="h-full min-h-[20px] rounded-md bg-gradient-to-r from-[#FFDE00] to-[#FF6200] transition-all duration-500 ease-in-out lg:min-h-[30px]"
                              style={{ width: `${remainingPercentage}%` }}
                            ></div>
                          </div>
                        </div>
                      );
                    })()}
                </div>
              </div>
            </div>
          </div>
        </div>
        {/* --- COLUMN 2: FORM & RATINGS SECTION --- */}
        <div
          className="flex min-h-screen flex-col justify-start overflow-y-auto bg-white px-0 pb-0 pt-4 lg:w-[520px] lg:flex-none lg:justify-center lg:border-l lg:border-[#e9ecef] lg:pt-8"
          ref={setFormSectionRef}
        >
          {allSubmissionsForTracker.length > 0 && (
            <SubmissionTracker
              submissions={allSubmissionsForTracker}
              onShowModal={handleShowModal}
              siteConfig={hydratedState.siteSettings}
            />
          )}

          {areSubmissionOpen ? (
            <SubmissionForm
              onSubmissionSuccess={onSubmissionSuccess}
              showSlug={showSlug}
              siteConfig={hydratedState.siteSettings}
              siteControls={hydratedState.siteControls}
              onStepChange={handleFormStepChange}
            />
          ) : (
            <div className="mx-auto w-[500px] max-w-[90%] rounded-2xl bg-[#f5f5f5] p-[35px] text-[#1a1a1a]">
              <p>{hydratedState.siteSettings.submissionsClosedCopy}</p>
            </div>
          )}

          {reviewedSubmissions.length > 0 && (
            <div className="mx-auto mt-[25px] w-[90%] max-w-[500px] bg-cover bg-center p-0 pb-5 text-center text-black">
              <h4 className="mt-0 border-b border-[#555] pb-[10px]">Recently Rated Takes</h4>
              <table className="w-full border-collapse text-left">
                <tbody>
                  {reviewedSubmissions.map((sub) => (
                    <tr
                      key={sub.id}
                      onClick={() => setModalSubmission(sub)}
                      tabIndex={0}
                      role="button"
                      onKeyDown={(e) => handleTableRowKeyDown(e, sub)}
                      className="cursor-pointer border-b border-[#444] hover:bg-white/10"
                    >
                      <td className="px-2 py-3">
                        {hydratedState.ratings.find(
                          (r) =>
                            r.id ===
                            (sub.specialRating || sub.rating?.toString())
                        )?.name || "Rated"}
                      </td>
                      <td className="px-2 py-3">
                        {(
                          sub.cleaned_text ||
                          sub.original_text ||
                          ""
                        ).substring(0, 30)}
                        ...
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {user && (
            <div className="my-8 text-center">
              <Link
                href="/producer"
                className="font-bold text-[#6c757d] underline"
              >
                Admin Link: Producer Dashboard
              </Link>
            </div>
          )}
          {hydratedState.siteSettings.footerTuneInCopyA && (
            <div className="mx-auto">
              <div className="mx-auto flex max-w-[520px] items-center gap-2 p-4">
                <div className="flex-1 text-[0.9rem] font-medium text-[#212529]">
                  {hydratedState.siteSettings.footerTuneInCopyA}
                </div>
                {hydratedState.siteSettings.footerChannelLogo1 && (
                  <div className="block">
                    <img
                      src={getImageURL(hydratedState.siteSettings.footerChannelLogo1)}
                      alt="Channel Logo 1"
                      className="block max-h-[50px] w-auto max-w-full"
                    />
                  </div>
                )}
                {hydratedState.siteSettings.footerTuneInCopyB && (
                  <div className="text-[0.9rem] font-medium text-[#212529]">
                    {hydratedState.siteSettings.footerTuneInCopyB}
                  </div>
                )}
                {hydratedState.siteSettings.footerChannelLogo2 && (
                  <div className="block">
                    <img
                      src={getImageURL(hydratedState.siteSettings.footerChannelLogo2)}
                      alt="Channel Logo 2"
                      className="block max-h-[50px] w-auto max-w-full"
                    />
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* These elements outside the two-column layout */}
      {/* ================================================================================= */}
      {/* RATING MODAL                                                                    */}
      {/* ================================================================================= */}

      <RatingShareModal
        submission={modalSubmission}
        siteSettings={hydratedState.siteSettings}
        ratings={hydratedState.ratings}
        showSlug={showSlug}
        onClose={handleCloseModal}
        onShare={handleShare}
      />

      {/* ================================================================================= */}
      {/* FOOTER                                                                          */}
      {/* ================================================================================= */}
      <footer className="bg-[#f8f9fa] px-4 py-8 text-center">
        <div className="mb-4 flex flex-wrap justify-center gap-x-4 gap-y-2 text-sm">
          <a href="https://www.siriusxm.com/sxm-web-terms-of-use" className="text-[#6c757d] hover:underline">
            Website Terms
          </a>
          <a href="https://www.siriusxm.com/customeragreement" className="text-[#6c757d] hover:underline">
            Customer Agreement
          </a>
          <a href="https://www.siriusxm.com/privacy-policy" className="text-[#6c757d] hover:underline">Privacy Policy</a>
          <a href="https://www.siriusxm.com/youradchoices" className="text-[#6c757d] hover:underline">Your Ad Choices</a>
          <a href="https://www.siriusxm.com/do-not-call" className="text-[#6c757d] hover:underline">Do Not Call Policy</a>
          <a href="http://www.siriusxm.com/yourprivacychoices" className="text-[#6c757d] hover:underline">
            Your Privacy Choices
          </a>
          <img
            className="inline-block h-4"
            src="https://www.siriusxm.com/content/dam/sxm-com/icons/ccpa-opt-out-icon/privacyoptions.svg"
            alt="CCPA Opt-Out Icon"
          />
          <a href="https://publicfiles.fcc.gov/sdars-profile/sirius/" className="text-[#6c757d] hover:underline">
            FCC Public File
          </a>
          <a href="https://www.siriusxm.com/fccinfo?intcmp=OTHER_NA_www:Home_FCCInfo" className="text-[#6c757d] hover:underline">
            FCC Info
          </a>
          <a className="ot-sdk-show-settings text-[#6c757d] hover:underline" href="https://www.siriusxm.com/">
            Manage Cookies
          </a>
        </div>
        <div className="text-sm text-[#6c757d]">©2025 Sirius XM Radio LLC</div>
      </footer>
    </div>
  );
}
